import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { ArrowLeft, CheckCircle, AlertTriangle, FileText, Clock, MapPin, User, ShieldAlert, Activity, Link as LinkIcon, Map, Target, FileSearch } from 'lucide-react';
import { ALUR_LAPORAN } from '../data/binda';
import { useStore, isOfficialProduct } from '../data/store';
import { UrgencyBadge } from '../components/UrgencyBadge';
import { StatusBadge } from '../components/StatusBadge';
import { TimelineTracker } from '../components/TimelineTracker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/Dialog';
import { toast } from 'sonner';
import { Role } from '../data/rbac';
export default function ReportDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const role = localStorage.getItem('bimcs_role') as Role || 'Super Admin Nasional';
  const reports = useStore(s => s.reports);
  const verifyReport = useStore(s => s.verifyReport);
  const escalateReport = useStore(s => s.escalateReport);
  const rejectReport = useStore(s => s.rejectReport);
  const analyzeReport = useStore(s => s.analyzeReport);
  const followUpReport = useStore(s => s.followUpReport);
  const approveReport = useStore(s => s.approveReport);
  const completeReport = useStore(s => s.completeReport);
  const submitReport = useStore(s => s.submitReport);
  const coordinatorForward = useStore(s => s.coordinatorForward);
  const approveBinda = useStore(s => s.approveBinda);
  const nationalAccept = useStore(s => s.nationalAccept);
  const nationalRevise = useStore(s => s.nationalRevise);
  const report = reports.find(r => r.id === id);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [coordinatorOpen, setCoordinatorOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [verifyNote, setVerifyNote] = useState('');
  const [coordinatorNote, setCoordinatorNote] = useState('');
  const [escalateReason, setEscalateReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const kpiHistory = useStore(s => s.kpiHistory);
  const reportKpi = kpiHistory.filter(k => k.target === report?.id);
  const relatedReports = reports.filter(r => r.id !== report?.id && (r.region === report?.region || r.category === report?.category)).slice(0, 2);
  if (!report) {
    return <div className="flex flex-col items-center justify-center h-full space-y-4" data-id="element-3511">
        <h2 className="text-xl font-mono text-muted-foreground" data-id="element-3512">
          Laporan tidak ditemukan
        </h2>
        <Button onClick={() => navigate('/reports')} variant="outline" data-id="element-3513">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-3514" /> Kembali ke Daftar
        </Button>
      </div>;
  }
  const handleVerify = () => {
    verifyReport(report.id, verifyNote);
    setVerifyOpen(false);
    toast.success('Laporan berhasil diverifikasi dan status diperbarui.');
  };
  const handleCoordinatorForward = () => {
    coordinatorForward(report.id, coordinatorNote);
    setCoordinatorOpen(false);
    toast.success('Laporan diteruskan ke Analis.');
  };
  const handleEscalate = () => {
    escalateReport(report.id, escalateReason);
    setEscalateOpen(false);
    toast.success('Laporan berhasil dieskalasi ke Pusat Komando.');
  };
  const handleReject = () => {
    if (rejectReason.trim()) {
      rejectReport(report.id, rejectReason);
      setRejectOpen(false);
      toast.success('Laporan ditolak. Alasan penolakan telah dicatat.');
    } else {
      toast.error('Alasan penolakan wajib diisi.');
    }
  };
  const handleAnalyze = () => {
    analyzeReport(report.id);
    toast.success('Laporan sedang dianalisis.');
    navigate(`/reports/${report.id}/analysis`);
  };
  const handleFollowUp = () => {
    followUpReport(report.id);
    toast.success('Laporan masuk tahap tindak lanjut.');
  };
  const handleApprove = () => {
    approveBinda(report.id, 'Disetujui oleh Kepala BINDA.');
    toast.success('Laporan disetujui dan diteruskan ke Nasional.');
  };
  const handleComplete = () => {
    completeReport(report.id);
    toast.success('Laporan ditandai selesai.');
  };
  const handleSubmitDraft = () => {
    submitReport(report.id);
    toast.success('Draf berhasil dikirim.');
  };
  // Role-based permissions
  const canCoordinate = role === 'Koordinator Wilayah' || role === 'Super Admin Nasional';
  const canVerify = role === 'Analis' || role === 'Admin BINDA' || role === 'Super Admin Nasional';
  const canAnalyze = role === 'Analis' || role === 'Super Admin Nasional';
  const canApprove = role === 'Kepala BINDA' || role === 'Wakil Kepala BINDA' || role === 'Super Admin Nasional';
  const canFollowUp = role === 'Koordinator Wilayah' || role === 'Admin BINDA' || role === 'Super Admin Nasional';
  const canComplete = role === 'Admin BINDA' || role === 'Super Admin Nasional' || role === 'Koordinator Wilayah';
  const canEscalate = role === 'Analis' || role === 'Kepala BINDA' || role === 'Super Admin Nasional';
  const canNationalReview = role === 'Administrator Nasional' || role === 'Super Admin Nasional' || role === 'Analis'; // Assuming national analyst uses the same role or Super Admin
  const [nationalReviewOpen, setNationalReviewOpen] = useState(false);
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [nationalAssessment, setNationalAssessment] = useState('');
  const [strategicAnalysis, setStrategicAnalysis] = useState('');
  const [nationalRiskAssessment, setNationalRiskAssessment] = useState('');
  const [executiveNotes, setExecutiveNotes] = useState('');
  const [revisionReason, setRevisionReason] = useState('');
  const handleNationalAccept = () => {
    if (!executiveSummary) {
      toast.error('Executive Summary wajib diisi.');
      return;
    }
    nationalAccept(report.id, {
      executiveSummary,
      nationalAssessment,
      strategicAnalysis,
      nationalRiskAssessment,
      executiveNotes
    });
    setNationalReviewOpen(false);
    toast.success('Laporan Diterima Nasional dan resmi menjadi Produk Intelijen.');
  };
  const handleNationalRevise = () => {
    if (!revisionReason) {
      toast.error('Alasan revisi wajib diisi.');
      return;
    }
    nationalRevise(report.id, revisionReason);
    setNationalReviewOpen(false);
    toast.success('Laporan dikembalikan ke BINDA untuk revisi.');
  };
  return <div className="space-y-6 animate-in fade-in duration-500 max-w-6xl mx-auto pb-12" data-id="element-3515">
      <div className="flex items-center gap-4 border-b border-border/50 pb-4" data-id="element-3516">
        <Button variant="ghost" size="icon" onClick={() => navigate('/reports')} className="hover:bg-primary/20 hover:text-primary" data-id="element-3517">
          <ArrowLeft className="w-5 h-5" data-id="element-3518" />
        </Button>
        <div data-id="element-3519">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground flex items-center gap-3" data-id="element-3520">
            DETAIL LAPORAN
            <span className="text-sm font-mono text-primary bg-primary/10 px-2 py-1 rounded border border-primary/30" data-id="element-3521">
              {report.id}
            </span>
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-3522">
            DENS CAKRA // Rekaman Intelijen
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3523">
        <div className="lg:col-span-2 space-y-6" data-id="element-3524">
          <Card className="glass-panel-heavy border-primary/30 shadow-[0_0_30px_rgba(var(--primary),0.1)]" data-id="element-3525">
            <CardHeader className="p-6 border-b border-border/50 bg-primary/5" data-id="element-3526">
              <div className="flex justify-between items-start" data-id="element-3527">
                <div data-id="element-3528">
                  <div className="flex items-center gap-3 mb-2 flex-wrap" data-id="element-3529">
                    <UrgencyBadge urgency={report.urgency} data-id="element-3530" />
                    <StatusBadge status={report.status} data-id="element-3531" />
                    {report.verificationStatus && <Badge variant="outline" className="text-[10px] font-mono border-primary/50 text-primary" data-id="element-3532">
                        Verifikasi: {report.verificationStatus}
                      </Badge>}
                    {report.followUpStatus && <Badge variant="outline" className="text-[10px] font-mono border-primary/50 text-primary" data-id="element-3533">
                        Tindak Lanjut: {report.followUpStatus}
                      </Badge>}
                  </div>
                  <CardTitle className="text-2xl font-heading leading-tight mt-2" data-id="element-3534">
                    {report.title}
                  </CardTitle>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-8" data-id="element-3535">
              {/* Metadata Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3536">
                <div className="bg-black/20 p-4 rounded border border-border/30" data-id="element-3537">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2 mb-2" data-id="element-3538">
                    <User className="w-4 h-4" data-id="element-3539" /> Pengirim
                  </span>
                  <span className="font-mono text-sm" data-id="element-3540">{report.sender}</span>
                </div>
                <div className="bg-black/20 p-4 rounded border border-border/30" data-id="element-3541">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2 mb-2" data-id="element-3542">
                    <Clock className="w-4 h-4" data-id="element-3543" /> Waktu
                  </span>
                  <span className="font-mono text-sm" data-id="element-3544">{report.time}</span>
                </div>
                <div className="bg-black/20 p-4 rounded border border-border/30" data-id="element-3545">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2 mb-2" data-id="element-3546">
                    <FileText className="w-4 h-4" data-id="element-3547" /> Tipe
                  </span>
                  <span className="text-sm" data-id="element-3548">{report.type}</span>
                </div>
                <div className="bg-black/20 p-4 rounded border border-border/30" data-id="element-3549">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest flex items-center gap-2 mb-2" data-id="element-3550">
                    <MapPin className="w-4 h-4" data-id="element-3551" /> Wilayah
                  </span>
                  <span className="font-mono text-sm" data-id="element-3552">{report.region}</span>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-6" data-id="element-3553">
                <div data-id="element-3554">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest block mb-2" data-id="element-3555">
                    Ringkasan Eksekutif
                  </span>
                  <div className="p-4 bg-black/30 rounded text-sm border border-border/30 leading-relaxed text-foreground/90" data-id="element-3556">
                    {report.ringkasan || report.description || 'Tidak ada ringkasan.'}
                  </div>
                </div>

                {report.kronologi && <div data-id="element-3557">
                    <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest block mb-2" data-id="element-3558">
                      Kronologi Kejadian
                    </span>
                    <div className="p-4 bg-black/30 rounded text-sm border border-border/30 leading-relaxed text-foreground/90 whitespace-pre-wrap" data-id="element-3559">
                      {report.kronologi}
                    </div>
                  </div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3560">
                  {report.pihakTerkait && <div data-id="element-3561">
                      <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest block mb-2" data-id="element-3562">
                        Pihak Terkait
                      </span>
                      <div className="p-4 bg-black/30 rounded text-sm border border-border/30 leading-relaxed text-foreground/90" data-id="element-3563">
                        {report.pihakTerkait}
                      </div>
                    </div>}
                  {report.potensiDampak && <div data-id="element-3564">
                      <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest block mb-2" data-id="element-3565">
                        Potensi Dampak
                      </span>
                      <div className="p-4 bg-black/30 rounded text-sm border border-border/30 leading-relaxed text-foreground/90" data-id="element-3566">
                        {report.potensiDampak}
                      </div>
                    </div>}
                </div>

                {report.rekomendasiAwal && <div data-id="element-3567">
                    <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest block mb-2" data-id="element-3568">
                      Rekomendasi Awal
                    </span>
                    <div className="p-4 bg-black/30 rounded text-sm border border-border/30 leading-relaxed text-foreground/90" data-id="element-3569">
                      {report.rekomendasiAwal}
                    </div>
                  </div>}
              </div>

              {/* Analyst Output (if any) */}
              {(report.intelligenceAssessment || report.threatClass || report.threatLevel || report.aiReport) && <div className="border-t border-border/50 pt-6" data-id="element-3570">
                  <h4 className="text-sm font-mono uppercase tracking-widest text-primary mb-4 flex items-center gap-2" data-id="element-3571">
                    <FileSearch className="w-4 h-4" data-id="element-3572" /> Laporan Intelijen Resmi
                  </h4>

                  {report.aiReport ? <div className="space-y-4" data-id="element-3573">
                      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-3574">
                        <div className="bg-primary/5 p-3 rounded border border-primary/20" data-id="element-3575">
                          <span className="text-[10px] font-mono text-primary uppercase tracking-widest block mb-1" data-id="element-3576">
                            Klasifikasi
                          </span>
                          <span className="font-bold text-destructive" data-id="element-3577">
                            {report.aiReport.classification}
                          </span>
                        </div>
                        <div className="bg-primary/5 p-3 rounded border border-primary/20" data-id="element-3578">
                          <span className="text-[10px] font-mono text-primary uppercase tracking-widest block mb-1" data-id="element-3579">
                            Nomor Laporan
                          </span>
                          <span className="font-bold" data-id="element-3580">
                            {report.aiReport.nomor}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3" data-id="element-3581">
                        <div data-id="element-3582">
                          <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest block mb-1" data-id="element-3583">
                            Fakta-Fakta
                          </span>
                          <div className="p-3 bg-black/30 rounded text-xs border border-border/30 leading-relaxed text-foreground/90 whitespace-pre-wrap" data-id="element-3584">
                            {report.aiReport.facts}
                          </div>
                        </div>
                        <div data-id="element-3585">
                          <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest block mb-1" data-id="element-3586">
                            Penilaian (Assessment)
                          </span>
                          <div className="p-3 bg-black/30 rounded text-xs border border-border/30 leading-relaxed text-foreground/90 whitespace-pre-wrap" data-id="element-3587">
                            {report.aiReport.assessment}
                          </div>
                        </div>
                        <div data-id="element-3588">
                          <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest block mb-1" data-id="element-3589">
                            Rekomendasi
                          </span>
                          <div className="p-3 bg-black/30 rounded text-xs border border-border/30 leading-relaxed text-foreground/90 whitespace-pre-wrap" data-id="element-3590">
                            {report.aiReport.recommendations}
                          </div>
                        </div>
                      </div>
                    </div> : <>
                      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-3591">
                        <div className="bg-primary/5 p-3 rounded border border-primary/20" data-id="element-3592">
                          <span className="text-[10px] font-mono text-primary uppercase tracking-widest block mb-1" data-id="element-3593">
                            Klasifikasi Ancaman
                          </span>
                          <span className="font-bold" data-id="element-3594">
                            {report.threatClass || '-'}
                          </span>
                        </div>
                        <div className="bg-primary/5 p-3 rounded border border-primary/20" data-id="element-3595">
                          <span className="text-[10px] font-mono text-primary uppercase tracking-widest block mb-1" data-id="element-3596">
                            Tingkat Ancaman
                          </span>
                          <span className="font-bold" data-id="element-3597">
                            {report.threatLevel || '-'}
                          </span>
                        </div>
                      </div>
                      {report.intelligenceAssessment && <div className="p-4 bg-primary/5 rounded text-sm border border-primary/20 leading-relaxed text-foreground/90 whitespace-pre-wrap" data-id="element-3598">
                          {report.intelligenceAssessment}
                        </div>}
                    </>}
                </div>}

              {/* Executive Summary (if any) */}
              {report.executiveSummary && <div className="border-t border-border/50 pt-6" data-id="element-3599">
                  <h4 className="text-sm font-mono uppercase tracking-widest text-[var(--status-standby)] mb-4 flex items-center gap-2" data-id="element-3600">
                    <Target className="w-4 h-4" data-id="element-3601" /> Executive Summary (Nasional)
                  </h4>
                  <div className="p-4 bg-[var(--status-standby)]/10 rounded text-sm border border-[var(--status-standby)]/30 leading-relaxed text-foreground/90 whitespace-pre-wrap" data-id="element-3602">
                    {report.executiveSummary}
                  </div>
                </div>}

              {/* Attachments & Map */}
              <div className="border-t border-border/50 pt-6" data-id="element-3603">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3604">
                  <div data-id="element-3605">
                    <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest block mb-4" data-id="element-3606">
                      Lampiran
                    </span>
                    <div className="flex gap-4 flex-wrap" data-id="element-3607">
                      {report.attachments ? report.attachments.map((att, i) => <div key={i} className="w-20 h-20 bg-black/40 rounded border border-border/50 flex flex-col items-center justify-center hover:border-primary/50 cursor-pointer transition-colors group" data-id="element-3608">
                            <FileText className="w-6 h-6 text-muted-foreground group-hover:text-primary mb-2" data-id="element-3609" />
                            <span className="text-[9px] font-mono text-muted-foreground text-center px-1 truncate w-full" data-id="element-3610">
                              {att.name}
                            </span>
                          </div>) : <div className="text-xs text-muted-foreground italic" data-id="element-3611">
                          Tidak ada lampiran.
                        </div>}
                    </div>
                  </div>
                  <div data-id="element-3612">
                    <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest block mb-4" data-id="element-3613">
                      Lokasi Geografis
                    </span>
                    <div className="h-24 bg-black/40 rounded border border-border/50 flex items-center justify-center relative overflow-hidden" data-id="element-3614">
                      <Map className="w-8 h-8 text-muted-foreground/30 absolute" data-id="element-3615" />
                      <div className="z-10 flex items-center gap-2 text-xs font-mono text-primary bg-background/80 px-3 py-1.5 rounded border border-primary/30" data-id="element-3616">
                        <MapPin className="w-3 h-3" data-id="element-3617" />
                        {report.geotag ? `${report.geotag.lat}, ${report.geotag.lng}` : 'Koordinat tidak tersedia'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-3618">
          {/* Actions */}
          <Card className="glass-panel border-border/50" data-id="element-3619">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-3620">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3621">
                <Activity className="w-4 h-4 text-primary" data-id="element-3622" /> Aksi Operasional
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3" data-id="element-3623">
              {report.stage === 'Draft' && report.sender === 'OP-772' && <Button onClick={handleSubmitDraft} className="w-full bg-primary text-primary-foreground font-mono text-xs" data-id="element-3624">
                  Kirim Laporan
                </Button>}

              {canCoordinate && report.stage === 'Dikirim' && <Dialog open={coordinatorOpen} onOpenChange={setCoordinatorOpen} data-id="element-3625">
                  <DialogTrigger asChild data-id="element-3626">
                    <Button className="w-full bg-primary/20 text-primary border border-primary/50 hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-widest" data-id="element-3627">
                      <CheckCircle className="w-4 h-4 mr-2" data-id="element-3628" /> Review
                      Koordinator
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-panel-heavy border-border/50" data-id="element-3629">
                    <DialogHeader data-id="element-3630">
                      <DialogTitle className="font-mono uppercase tracking-widest" data-id="element-3631">
                        Review Koordinator
                      </DialogTitle>
                      <DialogDescription className="font-mono text-xs" data-id="element-3632">
                        Tambahkan catatan dan teruskan ke Analis.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4" data-id="element-3633">
                      <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[80px]" placeholder="Catatan koordinator (opsional)..." value={coordinatorNote} onChange={e => setCoordinatorNote(e.target.value)} data-id="element-3634" />
                    </div>
                    <DialogFooter data-id="element-3635">
                      <Button variant="outline" onClick={() => setCoordinatorOpen(false)} className="font-mono text-xs" data-id="element-3636">
                        Batal
                      </Button>
                      <Button onClick={handleCoordinatorForward} className="font-mono text-xs bg-primary text-primary-foreground" data-id="element-3637">
                        Teruskan ke Analis
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>}

              {canVerify && (report.stage === 'Review Koordinator' || report.stage === 'Perlu Revisi') && <Dialog open={verifyOpen} onOpenChange={setVerifyOpen} data-id="element-3638">
                    <DialogTrigger asChild data-id="element-3639">
                      <Button className="w-full bg-[var(--status-active)] text-black hover:bg-[var(--status-active)]/80 font-mono text-xs uppercase tracking-widest" data-id="element-3640">
                        <CheckCircle className="w-4 h-4 mr-2" data-id="element-3641" /> Verifikasi
                        Laporan
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass-panel-heavy border-border/50" data-id="element-3642">
                      <DialogHeader data-id="element-3643">
                        <DialogTitle className="font-mono uppercase tracking-widest" data-id="element-3644">
                          Verifikasi Laporan
                        </DialogTitle>
                        <DialogDescription className="font-mono text-xs" data-id="element-3645">
                          Apakah data sudah valid dan sesuai?
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-4" data-id="element-3646">
                        <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[80px]" placeholder="Catatan verifikasi (opsional)..." value={verifyNote} onChange={e => setVerifyNote(e.target.value)} data-id="element-3647" />
                      </div>
                      <DialogFooter data-id="element-3648">
                        <Button variant="outline" onClick={() => setVerifyOpen(false)} className="font-mono text-xs" data-id="element-3649">
                          Batal
                        </Button>
                        <Button onClick={handleVerify} className="font-mono text-xs bg-[var(--status-active)] text-black" data-id="element-3650">
                          Setujui
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>}

              {canVerify && (report.stage === 'Review Koordinator' || report.stage === 'Diverifikasi' || report.stage === 'Perlu Revisi') && <Dialog open={rejectOpen} onOpenChange={setRejectOpen} data-id="element-3651">
                    <DialogTrigger asChild data-id="element-3652">
                      <Button variant="outline" className="w-full border-destructive/50 text-destructive hover:bg-destructive/10 font-mono text-xs uppercase tracking-widest" data-id="element-3653">
                        Tolak Laporan
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass-panel-heavy border-border/50" data-id="element-3654">
                      <DialogHeader data-id="element-3655">
                        <DialogTitle className="font-mono uppercase tracking-widest text-destructive" data-id="element-3656">
                          Tolak Laporan
                        </DialogTitle>
                        <DialogDescription className="font-mono text-xs" data-id="element-3657">
                          Berikan alasan penolakan laporan ini.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-4" data-id="element-3658">
                        <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[80px]" placeholder="Alasan penolakan (wajib)..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} data-id="element-3659" />
                      </div>
                      <DialogFooter data-id="element-3660">
                        <Button variant="outline" onClick={() => setRejectOpen(false)} className="font-mono text-xs" data-id="element-3661">
                          Batal
                        </Button>
                        <Button variant="destructive" onClick={handleReject} className="font-mono text-xs" data-id="element-3662">
                          Kirim Penolakan
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>}

              {canAnalyze && (report.stage === 'Diverifikasi' || report.stage === 'Analisis') && <Button onClick={handleAnalyze} className="w-full bg-primary/20 text-primary border border-primary/50 hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-widest" data-id="element-3663">
                    <FileSearch className="w-4 h-4 mr-2" data-id="element-3664" /> Analisis Intelijen
                  </Button>}

              {canApprove && report.stage === 'Analisis' && <Button onClick={handleApprove} className="w-full bg-[var(--status-active)] text-black hover:bg-[var(--status-active)]/80 font-mono text-xs uppercase tracking-widest" data-id="element-3665">
                  Setujui (Kepala BINDA)
                </Button>}

              {canNationalReview && report.stage === 'Disetujui BINDA' && !report.nationalReviewed && <Dialog open={nationalReviewOpen} onOpenChange={setNationalReviewOpen} data-id="element-3666">
                    <DialogTrigger asChild data-id="element-3667">
                      <Button className="w-full bg-primary/20 text-primary border border-primary/50 hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-widest" data-id="element-3668">
                        <Target className="w-4 h-4 mr-2" data-id="element-3669" /> Review Analis
                        Nasional
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="glass-panel-heavy border-border/50 max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar" data-id="element-3670">
                      <DialogHeader data-id="element-3671">
                        <DialogTitle className="font-mono uppercase tracking-widest" data-id="element-3672">
                          Review Analis Nasional
                        </DialogTitle>
                        <DialogDescription className="font-mono text-xs" data-id="element-3673">
                          Validasi akhir untuk menjadi Produk Intelijen Resmi.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-4 space-y-4" data-id="element-3674">
                        <Button variant="outline" size="sm" className="w-full text-[10px] font-mono border-primary/50 text-primary hover:bg-primary/10" onClick={() => {
                    setExecutiveSummary(`RINGKASAN EKSEKUTIF NASIONAL:\n\nBerdasarkan laporan intelijen dari ${report.region} terkait ${report.category}, situasi dinilai memiliki tingkat ancaman ${report.threatLevel || report.aiReport?.classification || 'Tinggi'}. \n\nLangkah mitigasi telah diinstruksikan kepada unit operasional terkait. Pemantauan ketat dilanjutkan selama 48 jam ke depan.`);
                    setNationalAssessment('Ancaman terkonfirmasi valid dan memerlukan atensi nasional.');
                    setStrategicAnalysis('Berdampak pada stabilitas regional.');
                    setNationalRiskAssessment('Risiko eskalasi tinggi jika tidak ditangani dalam 24 jam.');
                    toast.success('Draft Nasional berhasil di-generate oleh AI.');
                  }} data-id="element-3675">
                          <FileSearch className="w-3 h-3 mr-2" data-id="element-3676" /> Generate AI
                          Draft
                        </Button>

                        <div className="space-y-2" data-id="element-3677">
                          <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3678">
                            Executive Summary (Wajib)
                          </label>
                          <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[80px]" placeholder="Executive Summary..." value={executiveSummary} onChange={e => setExecutiveSummary(e.target.value)} data-id="element-3679" />
                        </div>

                        <div className="grid grid-cols-2 gap-4" data-id="element-3680">
                          <div className="space-y-2" data-id="element-3681">
                            <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3682">
                              National Assessment
                            </label>
                            <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[60px]" value={nationalAssessment} onChange={e => setNationalAssessment(e.target.value)} data-id="element-3683" />
                          </div>
                          <div className="space-y-2" data-id="element-3684">
                            <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3685">
                              Strategic Analysis
                            </label>
                            <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[60px]" value={strategicAnalysis} onChange={e => setStrategicAnalysis(e.target.value)} data-id="element-3686" />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4" data-id="element-3687">
                          <div className="space-y-2" data-id="element-3688">
                            <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3689">
                              National Risk Assessment
                            </label>
                            <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[60px]" value={nationalRiskAssessment} onChange={e => setNationalRiskAssessment(e.target.value)} data-id="element-3690" />
                          </div>
                          <div className="space-y-2" data-id="element-3691">
                            <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3692">
                              Executive Notes
                            </label>
                            <textarea className="w-full bg-black/40 border border-border/50 rounded p-3 text-xs font-mono focus:outline-none focus:border-primary/50 min-h-[60px]" value={executiveNotes} onChange={e => setExecutiveNotes(e.target.value)} data-id="element-3693" />
                          </div>
                        </div>

                        <div className="space-y-2" data-id="element-3694">
                          <label className="text-[10px] font-mono uppercase text-muted-foreground" data-id="element-3695">
                            Alasan Revisi (Jika Ditolak)
                          </label>
                          <textarea className="w-full bg-black/40 border border-destructive/30 rounded p-3 text-xs font-mono focus:outline-none focus:border-destructive/50 min-h-[60px]" placeholder="Isi jika memilih Kembalikan untuk Revisi..." value={revisionReason} onChange={e => setRevisionReason(e.target.value)} data-id="element-3696" />
                        </div>
                      </div>
                      <DialogFooter className="flex justify-between sm:justify-between" data-id="element-3697">
                        <Button variant="outline" onClick={handleNationalRevise} className="font-mono text-xs border-destructive/50 text-destructive hover:bg-destructive/10" data-id="element-3698">
                          Kembalikan (Revisi)
                        </Button>
                        <div className="flex gap-2" data-id="element-3699">
                          <Button variant="outline" onClick={() => setNationalReviewOpen(false)} className="font-mono text-xs" data-id="element-3700">
                            Batal
                          </Button>
                          <Button onClick={handleNationalAccept} className="font-mono text-xs bg-primary text-primary-foreground" data-id="element-3701">
                            Terima Nasional
                          </Button>
                        </div>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>}

              {canFollowUp && (report.stage === 'Diterima Nasional' || report.stage === 'Tindak Lanjut') && <Button onClick={handleFollowUp} className="w-full bg-primary/20 text-primary border border-primary/50 hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-widest" data-id="element-3702">
                    Proses Tindak Lanjut
                  </Button>}

              {canComplete && (report.stage === 'Diterima Nasional' || report.stage === 'Tindak Lanjut') && <Button onClick={handleComplete} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-mono text-xs uppercase tracking-widest" data-id="element-3703">
                    Tandai Selesai
                  </Button>}

              {canEscalate && report.stage === 'Diterima Nasional' && <Dialog open={escalateOpen} onOpenChange={setEscalateOpen} data-id="element-3704">
                  <DialogTrigger asChild data-id="element-3705">
                    <Button variant="destructive" className="w-full font-mono text-xs uppercase tracking-widest bg-destructive/20 text-destructive border border-destructive/50 hover:bg-destructive/40" data-id="element-3706">
                      <AlertTriangle className="w-4 h-4 mr-2" data-id="element-3707" /> Eskalasi ke
                      Pusat
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-panel-heavy border-destructive/50" data-id="element-3708">
                    <DialogHeader data-id="element-3709">
                      <DialogTitle className="font-mono uppercase tracking-widest text-destructive" data-id="element-3710">
                        Eskalasi Ancaman
                      </DialogTitle>
                      <DialogDescription className="font-mono text-xs" data-id="element-3711">
                        Eskalasi laporan ini ke Pusat Komando Nasional.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="py-4" data-id="element-3712">
                      <textarea className="w-full bg-black/40 border border-destructive/30 rounded p-3 text-xs font-mono focus:outline-none focus:border-destructive/50 min-h-[80px]" placeholder="Alasan eskalasi..." value={escalateReason} onChange={e => setEscalateReason(e.target.value)} data-id="element-3713" />
                    </div>
                    <DialogFooter data-id="element-3714">
                      <Button variant="outline" onClick={() => setEscalateOpen(false)} className="font-mono text-xs" data-id="element-3715">
                        Batal
                      </Button>
                      <Button variant="destructive" onClick={handleEscalate} className="font-mono text-xs" data-id="element-3716">
                        Konfirmasi Eskalasi
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>}
            </CardContent>
          </Card>

          {/* KPI Impact */}
          <Card className="glass-panel border-border/50" data-id="element-3717">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-3718">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3719">
                <Target className="w-4 h-4 text-primary" data-id="element-3720" /> Dampak KPI
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3" data-id="element-3721">
              {reportKpi.length > 0 ? reportKpi.map((k, i) => <div key={i} className="flex justify-between items-center text-xs font-mono border-b border-border/30 pb-2 last:border-0 last:pb-0" data-id="element-3722">
                    <div className="flex flex-col" data-id="element-3723">
                      <span className="text-foreground" data-id="element-3724">{k.reason}</span>
                      <span className="text-[10px] text-muted-foreground" data-id="element-3725">
                        {k.time}
                      </span>
                    </div>
                    <span className="text-green-400 font-bold" data-id="element-3726">
                      +{k.delta} Poin
                    </span>
                  </div>) : <div className="text-xs font-mono text-muted-foreground italic text-center py-2" data-id="element-3727">
                  {isOfficialProduct(report) ? 'Menghitung dampak KPI...' : 'Menunggu status Diterima Nasional untuk perhitungan KPI.'}
                </div>}
            </CardContent>
          </Card>

          {/* Related Reports */}
          <Card className="glass-panel border-border/50" data-id="element-3728">
            <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-3729">
              <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3730">
                <LinkIcon className="w-4 h-4 text-primary" data-id="element-3731" /> Laporan Terkait
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3" data-id="element-3732">
              {relatedReports.length > 0 ? <>
                  <div className="text-xs font-mono text-muted-foreground italic mb-2" data-id="element-3733">
                    Sistem mendeteksi {relatedReports.length} laporan dengan
                    pola serupa.
                  </div>
                  {relatedReports.map(rr => <div key={rr.id} onClick={() => navigate(`/reports/${rr.id}`)} className="p-3 bg-black/30 border border-border/30 rounded cursor-pointer hover:border-primary/50 transition-colors" data-id="element-3734">
                      <div className="flex justify-between items-start mb-1" data-id="element-3735">
                        <div className="text-xs text-primary font-mono" data-id="element-3736">
                          {rr.id}
                        </div>
                        <UrgencyBadge urgency={rr.urgency as any} className="text-[10px] px-1.5 py-0" data-id="element-3737" />
                      </div>
                      <div className="text-xs text-foreground/90 truncate font-medium" data-id="element-3738">
                        {rr.title}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2" data-id="element-3739">
                        <span data-id="element-3740">{rr.region}</span>
                        <span data-id="element-3741">•</span>
                        <span data-id="element-3742">{rr.category}</span>
                      </div>
                    </div>)}
                </> : <div className="text-xs font-mono text-muted-foreground italic text-center py-2" data-id="element-3743">
                  Tidak ada laporan terkait yang ditemukan.
                </div>}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Alur Laporan — full width horizontal, lega */}
      <Card className="glass-panel border-border/50" data-id="element-3744">
        <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-3745">
          <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-3746">
            <Clock className="w-4 h-4 text-primary" data-id="element-3747" /> Alur Laporan
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 md:p-8" data-id="element-3748">
          <TimelineTracker stages={ALUR_LAPORAN} currentStage={report.stage || 'Draft'} meta={report.timeline?.reduce((acc, entry) => {
          acc[entry.stage] = {
            time: entry.time,
            actor: entry.actor,
            note: entry.note
          };
          return acc;
        }, {} as Record<string, any>) || {}} data-id="element-3749" />
        </CardContent>
      </Card>
    </div>;
}